import React, { useEffect, useRef, useState } from 'react';
import './App.css';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import CardContent from '@mui/material/CardContent';
import CardMedia from '@mui/material/CardMedia';
import Container from '@mui/material/Container';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Fade from '@mui/material/Fade';
import GraphicEqIcon from '@mui/icons-material/GraphicEq';
import PauseCircleOutlinedIcon from '@mui/icons-material/PauseCircleOutlined';
import PlayCircleOutlinedIcon from '@mui/icons-material/PlayCircleOutlined';
import {
  AUDIO_EXAMPLES,
  CONTACT_EMAIL,
  GALLERY,
  HERO_IMAGE,
  PROJECTS,
  // SERVICES,
  STATS,
  TESTIMONIALS,
} from './siteContent';

const MAILTO = `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(
  'Double-K Audio — project inquiry'
)}`;

/** In-page scroll duration (ms); tuned slower than native `scroll-behavior: smooth`. */
const SECTION_SCROLL_MS = 2400;

function prefersReducedMotion() {
  return typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function easeInOutCubic(t) {
  return t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2;
}

function scrollToSectionHash(hash, durationMs = SECTION_SCROLL_MS) {
  const id = hash.startsWith('#') ? hash.slice(1) : hash;
  const el = document.getElementById(id);
  if (!el) return;

  const nav = document.querySelector('.App-nav');
  const offset = (nav?.offsetHeight ?? 0) + 12;
  const startY = window.scrollY;
  const targetY = Math.max(0, el.getBoundingClientRect().top + startY - offset);
  const distance = targetY - startY;
  const idHash = `#${id}`;
  const syncHash = () => {
    if (window.location.hash !== idHash) {
      window.history.pushState(null, '', idHash);
    }
  };

  if (Math.abs(distance) < 2) {
    syncHash();
    return;
  }

  if (prefersReducedMotion()) {
    window.scrollTo({ top: targetY });
    syncHash();
    return;
  }

  const start = performance.now();
  const step = (now) => {
    const t = Math.min(1, (now - start) / durationMs);
    window.scrollTo(0, startY + distance * easeInOutCubic(t));
    if (t < 1) {
      requestAnimationFrame(step);
    } else {
      syncHash();
    }
  };
  requestAnimationFrame(step);
}

const GALLERY_ROTATE_MS = 4500;

function ImageRotator({ images, alt, onExpand }) {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (images.length <= 1) return undefined;
    const id = window.setInterval(() => {
      setIndex((i) => (i + 1) % images.length);
    }, GALLERY_ROTATE_MS);
    return () => window.clearInterval(id);
  }, [images.length]);

  const interactive = typeof onExpand === 'function';
  const handleClick = interactive ? () => onExpand(images[index]) : undefined;

  return (
    <Box
      onClick={handleClick}
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      onKeyDown={
        interactive
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onExpand(images[index]);
              }
            }
          : undefined
      }
      aria-label={interactive ? `Expand ${alt}` : undefined}
      sx={{
        position: 'relative',
        width: '100%',
        aspectRatio: '16/10',
        overflow: 'hidden',
        cursor: interactive ? 'zoom-in' : 'default',
      }}
    >
      {images.map((src, i) => (
        <Fade key={src} in={i === index} timeout={600}>
          <Box
            component="img"
            src={src}
            alt={alt}
            sx={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          />
        </Fade>
      ))}
    </Box>
  );
}

const TESTIMONIAL_ROTATE_MS = 8000;

function TestimonialRotator({ items }) {
  const [index, setIndex] = useState(0);
  const list = items ?? [];

  useEffect(() => {
    setIndex((i) => (list.length ? i % list.length : 0));
  }, [list.length]);

  useEffect(() => {
    if (list.length <= 1) return undefined;
    const id = window.setInterval(() => {
      setIndex((i) => (i + 1) % list.length);
    }, TESTIMONIAL_ROTATE_MS);
    return () => window.clearInterval(id);
  }, [list.length]);

  if (!list.length) {
    return null;
  }

  const current = list[index % list.length];

  return (
    <Card
      variant="outlined"
      sx={{
        mb: 6,
        p: { xs: 2, sm: 3 },
        borderLeft: '4px solid',
        borderLeftColor: 'primary.main',
      }}
    >
      <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700, mb: 1.5, display: 'block' }}>
        What people say
      </Typography>
      <Box aria-live="polite" aria-atomic="true">
        <Fade in timeout={420} key={index}>
          <Box>
            <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.75, mb: 2 }}>
              “{current.quote}”
            </Typography>
            <Typography variant="body2" color="text.secondary">
              — {current.attribution}
            </Typography>
          </Box>
        </Fade>
      </Box>
      {list.length > 1 ? (
        <Box
          component="nav"
          aria-label="Testimonial navigation"
          sx={{ mt: 2.5, display: 'flex', gap: 1, justifyContent: 'flex-start', flexWrap: 'wrap' }}
        >
          {list.map((_, i) => (
            <Box
              key={i}
              component="button"
              type="button"
              aria-current={i === index ? 'true' : undefined}
              aria-label={`Quote ${i + 1} of ${list.length}`}
              onClick={() => setIndex(i)}
              sx={{
                p: 0,
                width: i === index ? 22 : 8,
                minWidth: 8,
                height: 8,
                borderRadius: 1,
                border: 'none',
                cursor: 'pointer',
                bgcolor: i === index ? 'primary.main' : 'rgba(255,255,255,0.2)',
                transition: 'width 0.2s ease, background-color 0.2s ease',
                '&:hover': {
                  bgcolor: i === index ? 'primary.main' : 'rgba(255,255,255,0.35)',
                },
              }}
            />
          ))}
        </Box>
      ) : null}
    </Card>
  );
}

const WAVE_BARS = [
  5, 12, 8, 15, 6, 14, 9, 11, 7, 16, 5, 13, 10, 8, 14, 6, 12, 9, 15, 7, 11,
  8, 13, 6,
];

function SectionHeading({ kicker, title, subtitle, id }) {
  return (
    <Stack id={id} spacing={1} sx={{ mb: 2.5 }}>
      {kicker ? (
        <Typography variant="overline" color="text.secondary">
          {kicker}
        </Typography>
      ) : null}
      <Typography variant="h2" component="h2" sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem' } }}>
        {title}
      </Typography>
      {subtitle ? (
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 640, lineHeight: 1.7 }}>
          {subtitle}
        </Typography>
      ) : null}
    </Stack>
  );
}

function WaveformDecor({ progress = 0, onSeek }) {
  const interactive = typeof onSeek === 'function';
  const handleClick = interactive
    ? (e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const fraction = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
        onSeek(fraction);
      }
    : undefined;
  return (
    <Box
      aria-hidden={!interactive}
      onClick={handleClick}
      role={interactive ? 'slider' : undefined}
      aria-valuemin={interactive ? 0 : undefined}
      aria-valuemax={interactive ? 100 : undefined}
      aria-valuenow={interactive ? Math.round(progress * 100) : undefined}
      sx={{
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
        gap: 0.35,
        height: 40,
        opacity: 0.85,
        cursor: interactive ? 'pointer' : 'default',
        userSelect: 'none',
      }}
    >
      {WAVE_BARS.map((h, i) => {
        const played = (i + 0.5) / WAVE_BARS.length <= progress;
        return (
          <Box
            key={i}
            sx={{
              width: 4,
              borderRadius: 1,
              height: `${h}px`,
              bgcolor: 'primary.main',
              opacity: played ? 1 : 0.25,
              transition: 'opacity 80ms linear',
            }}
          />
        );
      })}
    </Box>
  );
}

function formatDuration(seconds) {
  if (!Number.isFinite(seconds)) return '';
  const total = Math.round(seconds);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function App() {
  const audioRef = useRef(null);
  const pendingSeekRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [durations, setDurations] = useState(() => AUDIO_EXAMPLES.map(() => null));
  const [expandedImage, setExpandedImage] = useState(null);

  useEffect(() => {
    const probes = AUDIO_EXAMPLES.map((ex, i) => {
      const probe = new Audio();
      probe.preload = 'metadata';
      const onLoaded = () => {
        setDurations((prev) => {
          const next = prev.slice();
          next[i] = probe.duration;
          return next;
        });
      };
      probe.addEventListener('loadedmetadata', onLoaded);
      probe.src = ex.src;
      return { probe, onLoaded };
    });
    return () => {
      probes.forEach(({ probe, onLoaded }) => {
        probe.removeEventListener('loadedmetadata', onLoaded);
        probe.src = '';
      });
    };
  }, []);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return undefined;
    const handleLoaded = () => {
      if (pendingSeekRef.current != null && Number.isFinite(audio.duration)) {
        audio.currentTime = pendingSeekRef.current * audio.duration;
        pendingSeekRef.current = null;
      }
    };
    audio.addEventListener('loadedmetadata', handleLoaded);
    return () => audio.removeEventListener('loadedmetadata', handleLoaded);
  }, []);

  const ensureLoaded = (index) => {
    const audio = audioRef.current;
    const ex = AUDIO_EXAMPLES[index];
    const target = window.location.origin + ex.src;
    if (audio.src !== target) {
      audio.src = ex.src;
    }
  };

  const toggleClip = (index) => {
    const audio = audioRef.current;
    if (!audio) return;
    if (activeIndex === index && isPlaying) {
      audio.pause();
      return;
    }
    ensureLoaded(index);
    setActiveIndex(index);
    audio.play().catch(() => setIsPlaying(false));
  };

  const seekClip = (index, fraction) => {
    const audio = audioRef.current;
    if (!audio) return;
    const target = window.location.origin + AUDIO_EXAMPLES[index].src;
    const sameTrack = audio.src === target && Number.isFinite(audio.duration);
    if (sameTrack) {
      audio.currentTime = fraction * audio.duration;
      setCurrentTime(audio.currentTime);
      if (audio.paused) audio.play().catch(() => {});
      return;
    }
    pendingSeekRef.current = fraction;
    ensureLoaded(index);
    setActiveIndex(index);
    audio.play().catch(() => setIsPlaying(false));
  };

  return (
    <Box className="App-root">
      <audio
        ref={audioRef}
        preload="none"
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onEnded={() => {
          setIsPlaying(false);
          setCurrentTime(0);
        }}
        onTimeUpdate={(e) => setCurrentTime(e.currentTarget.currentTime)}
      />
      <AppBar
        position="sticky"
        elevation={0}
        className="App-nav"
        sx={{
          bgcolor: 'rgba(10, 9, 8, 0.72)',
          backdropFilter: 'blur(12px)',
          borderBottom: '1px solid rgba(255,255,255,0.06)',
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between', gap: 2, py: 1 }}>
          <Typography
            component="a"
            href="#top"
            variant="subtitle1"
            onClick={(e) => {
              e.preventDefault();
              if (prefersReducedMotion()) {
                window.scrollTo({ top: 0 });
              } else {
                const startY = window.scrollY;
                const start = performance.now();
                const step = (now) => {
                  const t = Math.min(1, (now - start) / SECTION_SCROLL_MS);
                  window.scrollTo(0, startY * (1 - easeInOutCubic(t)));
                  if (t < 1) requestAnimationFrame(step);
                };
                requestAnimationFrame(step);
              }
              if (window.location.hash) {
                window.history.pushState(null, '', window.location.pathname);
              }
            }}
            sx={{
              fontWeight: 700,
              letterSpacing: '-0.02em',
              color: 'inherit',
              textDecoration: 'none',
              cursor: 'pointer',
            }}
          >
            Double-K Audio
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: { xs: 0.5, sm: 1 } }}>
            <Button
              color="inherit"
              href="#examples"
              size="small"
              sx={{ textTransform: 'none' }}
              onClick={(e) => {
                e.preventDefault();
                scrollToSectionHash('#examples');
              }}
            >
              Examples
            </Button>
            <Button
              color="inherit"
              href="#projects"
              size="small"
              sx={{ textTransform: 'none' }}
              onClick={(e) => {
                e.preventDefault();
                scrollToSectionHash('#projects');
              }}
            >
              Projects
            </Button>
            <Button
              href={MAILTO}
              variant="contained"
              size="small"
              sx={{ textTransform: 'none', fontWeight: 600, display: { xs: 'none', sm: 'inline-flex' } }}
            >
              Book
            </Button>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Hero */}
      <Box className="App-hero" sx={{ position: 'relative' }}>
        <Box
          className="App-heroMedia"
          sx={{
            backgroundImage: `linear-gradient(105deg, rgba(10,9,8,0.92) 0%, rgba(10,9,8,0.55) 42%, rgba(10,9,8,0.25) 100%), url(${HERO_IMAGE})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, py: { xs: 6, sm: 10 } }}>
          <Stack spacing={2.5} sx={{ maxWidth: { md: 'min(640px, 92%)' } }}>
            <Typography variant="overline" color="primary.main" sx={{ fontWeight: 700 }}>
              For artists, crews & rooms that need to sound right
            </Typography>
            <Typography
              variant="h1"
              component="h1"
              sx={{
                fontSize: { xs: '2.25rem', sm: '3.15rem' },
                textShadow: '0 2px 28px rgba(0,0,0,0.45)',
              }}
            >
              Studio sound. Live reliability.
            </Typography>
            <Typography
              variant="h5"
              component="p"
              color="text.secondary"
              sx={{
                fontWeight: 400,
                fontSize: { xs: '1.05rem', sm: '1.2rem' },
                lineHeight: 1.55,
                maxWidth: 540,
              }}
            >
              Tracking, mixing, and tour-ready rigs — built so performances feel effortless and mixes
              translate everywhere your fans listen.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ pt: 1 }}>
              <Button
                href={MAILTO}
                variant="contained"
                size="large"
                sx={{ textTransform: 'none', fontWeight: 700, px: 3 }}
              >
                Plan a session
              </Button>
              <Button
                href="#examples"
                variant="outlined"
                size="large"
                color="inherit"
                startIcon={<GraphicEqIcon />}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  borderColor: 'rgba(255,255,255,0.28)',
                  '&:hover': { borderColor: 'rgba(255,255,255,0.45)' },
                }}
                onClick={(e) => {
                  e.preventDefault();
                  scrollToSectionHash('#examples');
                }}
              >
                Hear examples
              </Button>
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ opacity: 0.85 }}>
              Singles, EPs, weekend runs, and tour support — ask about immersive mixes and line-check
              documentation.
            </Typography>
          </Stack>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: { xs: 4, sm: 5 } }}>
        {/* Stats */}
        <Card variant="outlined" sx={{ mb: 5, px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 2.5 } }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: { xs: 2, sm: 0 },
              justifyContent: 'space-between',
            }}
          >
            {STATS.map((s, i) => (
              <React.Fragment key={s.label}>
                {i > 0 ? (
                  <Box
                    sx={{
                      display: { xs: 'none', sm: 'block' },
                      width: '1px',
                      alignSelf: 'stretch',
                      bgcolor: 'divider',
                      mx: { sm: 2 },
                    }}
                  />
                ) : null}
                <Stack spacing={0.5} sx={{ textAlign: { xs: 'left', sm: 'center' }, flex: 1 }}>
                  <Typography variant="h3" sx={{ fontSize: { xs: '2rem', sm: '2.35rem' }, fontWeight: 700 }}>
                    {s.value}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {s.label}
                  </Typography>
                </Stack>
              </React.Fragment>
            ))}
          </Box>
        </Card>

        {/* Gallery */}
        <SectionHeading
          title="Builds & Gear"
          subtitle="As an unabashed gearhead, I'm always building and rebuilding processing racks, pedalboards and other stage gear. Here are some rigs past and present."
        />
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
            gap: 1.5,
            mb: 6,
          }}
        >
          {GALLERY.map((item) => {
            const images = item.images?.length ? item.images : [item.src];
            const expand = (src) => setExpandedImage({ src, alt: item.alt });
            return (
              <Card key={item.caption} variant="outlined" sx={{ overflow: 'hidden', p: 0 }}>
                {images.length > 1 ? (
                  <ImageRotator images={images} alt={item.alt} onExpand={expand} />
                ) : (
                  <CardMedia
                    component="img"
                    image={images[0]}
                    alt={item.alt}
                    onClick={() => expand(images[0])}
                    sx={{ aspectRatio: '16/10', objectFit: 'cover', cursor: 'zoom-in' }}
                  />
                )}
                <CardContent sx={{ py: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.65 }}>
                    {item.caption}
                  </Typography>
                </CardContent>
              </Card>
            );
          })}
        </Box>

        {/* Services — hidden for now, restore when more content is ready */}
        {/*
        <SectionHeading
          kicker="What you get"
          title="Engineering, mixing & rigs — outcome first"
          subtitle="Each engagement is scoped around what the audience should feel and what your crew needs to repeat the win tomorrow night."
          id="services"
        />
        <Stack spacing={2} sx={{ mb: 6 }}>
          {SERVICES.map((item) => (
            <Card key={item.title} variant="outlined" sx={{ p: 0, overflow: 'hidden' }}>
              <Stack direction={{ xs: 'column', md: 'row' }}>
                <CardMedia
                  component="img"
                  image={item.image}
                  alt={item.alt}
                  sx={{
                    width: { md: 320 },
                    maxHeight: { xs: 220, md: 'none' },
                    objectFit: 'cover',
                  }}
                />
                <CardContent sx={{ p: { xs: 2, sm: 3 }, flex: 1 }}>
                  <Typography variant="overline" color="primary.main" sx={{ fontWeight: 700 }}>
                    {item.title}
                  </Typography>
                  <Typography variant="h6" component="h3" sx={{ mt: 0.5, mb: 1 }}>
                    {item.outcome}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ lineHeight: 1.75, whiteSpace: 'pre-line' }}
                  >
                    {item.blurb}
                  </Typography>
                </CardContent>
              </Stack>
            </Card>
          ))}
        </Stack>
        */}

        {/* Audio examples */}
        <SectionHeading
          title="Listen"
          subtitle="Some of my recent mixes. Feel free to reach out for more examples."
          id="examples"
        />
        <Stack spacing={1.5} sx={{ mb: 6 }}>
          {AUDIO_EXAMPLES.map((ex, i) => (
            <Card key={ex.title} variant="outlined" sx={{ p: 0 }}>
              <CardContent
                sx={{
                  py: 2,
                  px: { xs: 2, sm: 2.5 },
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: { sm: 'center' },
                  gap: 2,
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => toggleClip(i)}
                      aria-label={activeIndex === i && isPlaying ? `Pause ${ex.title}` : `Play ${ex.title}`}
                      sx={{ p: 0 }}
                    >
                      {activeIndex === i && isPlaying ? (
                        <PauseCircleOutlinedIcon fontSize="small" />
                      ) : (
                        <PlayCircleOutlinedIcon fontSize="small" />
                      )}
                    </IconButton>
                    <Typography variant="subtitle1" fontWeight={700} noWrap>
                      {ex.title}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {ex.detail}
                  </Typography>
                </Box>
                <WaveformDecor
                  progress={
                    activeIndex === i && durations[i]
                      ? Math.min(1, currentTime / durations[i])
                      : 0
                  }
                  onSeek={(fraction) => seekClip(i, fraction)}
                />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}
                >
                  {durations[i] != null
                    ? `${formatDuration(activeIndex === i ? currentTime : 0)} / ${formatDuration(durations[i])}`
                    : ''}
                </Typography>
              </CardContent>
            </Card>
          ))}
        </Stack>

        {/* Projects */}
        <SectionHeading
          kicker="Bands & releases"
          title="My Projects"
          subtitle="I play guitar and MD for Kim Beyer. I am a founding member of Say.Goodnight. and I produce and hype for local nerdcore rapper AJB."
          id="projects"
        />
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
            gap: 1.5,
            mb: 6,
          }}
        >
          {PROJECTS.map((p) => {
            const body = (
              <>
                <CardMedia component="img" image={p.image} alt={p.alt} sx={{ aspectRatio: '4/3', objectFit: 'cover' }} />
                <CardContent sx={{ py: 2 }}>
                  <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700 }}>
                    {p.tag}
                  </Typography>
                  <Typography variant="h6" component="h3" sx={{ mt: 0.5, mb: 0.75 }}>
                    {p.title}
                  </Typography>
                  <Typography variant="body2" color="primary.main" sx={{ mb: 1, fontWeight: 600 }}>
                    {p.outcome}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ lineHeight: 1.65, whiteSpace: 'pre-line' }}
                  >
                    {p.blurb}
                  </Typography>
                </CardContent>
              </>
            );
            return (
              <Card key={p.title} variant="outlined" sx={{ p: 0, overflow: 'hidden', height: '100%' }}>
                {p.link ? (
                  <CardActionArea
                    component="a"
                    href={p.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ height: '100%', display: 'block' }}
                  >
                    {body}
                  </CardActionArea>
                ) : (
                  body
                )}
              </Card>
            );
          })}
        </Box>

        {/* Testimonial */}
        <TestimonialRotator items={TESTIMONIALS} />

        {/* Contact */}
        <Card
          id="contact"
          variant="outlined"
          sx={{
            p: { xs: 2.5, sm: 4 },
            textAlign: { xs: 'left', sm: 'center' },
            background:
              'linear-gradient(180deg, rgba(232,184,109,0.08) 0%, rgba(255,255,255,0.02) 100%)',
          }}
        >
          <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700 }}>
            Next step
          </Typography>
          <Typography variant="h5" component="h2" sx={{ mt: 1, mb: 1.5 }}>
            Tell me about the show, the session, or the rig problem
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5, maxWidth: 520, mx: 'auto' }}>
            I want to hear about your next project and help you make it a success. Reach out below with details and I'll get back to you with a plan.          
          </Typography>
          <Button href={MAILTO} variant="contained" size="large" sx={{ textTransform: 'none', fontWeight: 700, px: 4 }}>
            Email {CONTACT_EMAIL}
          </Button>
        </Card>
      </Container>

      <Dialog
        open={!!expandedImage}
        onClose={() => setExpandedImage(null)}
        maxWidth="lg"
        PaperProps={{
          sx: {
            bgcolor: 'transparent',
            boxShadow: 'none',
            m: { xs: 1, sm: 2 },
            cursor: 'zoom-out',
          },
        }}
        onClick={() => setExpandedImage(null)}
      >
        {expandedImage ? (
          <Box
            component="img"
            src={expandedImage.src}
            alt={expandedImage.alt}
            sx={{
              display: 'block',
              maxWidth: '100%',
              maxHeight: '90vh',
              width: 'auto',
              height: 'auto',
              objectFit: 'contain',
            }}
          />
        ) : null}
      </Dialog>

      <Box component="footer" className="App-footer" sx={{ py: 3, textAlign: 'center' }}>
        <Container maxWidth="lg">
          <Typography variant="body2" color="text.secondary">
            © {new Date().getFullYear()} Double-K Audio ·{' '}
            <Link href={MAILTO} color="inherit" underline="hover">
              Contact
            </Link>
          </Typography>
        </Container>
      </Box>
    </Box>
  );
}

export default App;
